import React from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { SvgLogo } from '@actual-app/components/icons/logo';
import { Paragraph } from '@actual-app/components/paragraph';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { createBudget } from '#budgetfiles/budgetfilesSlice';
import { useServerURL } from '#components/ServerContext';
import { useNavigate } from '#hooks/useNavigate';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { segnaBenvenutoDemoInAttesa } from '#util/demo';

const fadeInUp = keyframes({
  from: { opacity: 0, transform: 'translateY(12px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

function entrance(delay: number) {
  return css({
    animationName: fadeInUp,
    animationDuration: '500ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'backwards',
    animationDelay: `${delay}ms`,
  });
}

export function WelcomeScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const serverURL = useServerURL();
  const { isNarrowWidth } = useResponsive();
  const isReducedMotion = useReducedMotion();

  const entranceClass = (delay: number) =>
    isReducedMotion ? undefined : entrance(delay);

  const demoButtonStyle = {
    fontSize: 19,
    fontWeight: 600,
    padding: '20px 24px',
    flexShrink: 0,
    ...(isNarrowWidth && { minHeight: styles.mobileMinHeight }),
  };

  const secondaryStyle = {
    fontSize: 13.5,
    padding: '8px 10px',
    color: theme.pageTextLink,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    ...(isNarrowWidth && { minHeight: styles.mobileMinHeight }),
  };

  return (
    <View
      style={{
        alignItems: 'center',
        gap: isNarrowWidth ? 25 : 12,
        width: '100%',
        maxWidth: 480,
        fontSize: 15,
        maxHeight: '100%',
        overflowY: 'auto',
        paddingTop: 20,
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
      }}
    >
      <View
        className={entranceClass(0)}
        style={{ alignItems: 'center', gap: 10, flexShrink: 0 }}
      >
        <SvgLogo
          width={45}
          height={45}
          style={{ color: theme.pageTextPositive }}
        />
        <Text style={{ ...styles.veryLargeText, textAlign: 'center' }}>
          <Trans>Welcome to Contabile</Trans>
        </Text>
        <Text
          style={{
            color: theme.pageTextLight,
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          <Trans>Your books, always in order</Trans>
        </Text>
      </View>

      <View
        className={entranceClass(150)}
        style={{ alignItems: 'center', flexShrink: 0 }}
      >
        <Paragraph
          style={{ textAlign: 'center', maxWidth: 400, marginBottom: 0 }}
        >
          <Trans>
            Contabile is an envelope budgeting app for personal and small
            business accounts. It is free and open source: your data stays on
            this device, nothing is collected and there is no account to create.
          </Trans>
        </Paragraph>
      </View>

      {/* The demo is the front door. It has to read as the one thing to click:
          full width, twice the height of anything else, first in the tab order.
          Everything below it is deliberately quieter. */}
      <View
        className={entranceClass(300)}
        style={{
          width: '100%',
          maxWidth: 400,
          gap: 8,
          flexShrink: 0,
          marginTop: 10,
        }}
      >
        <Button
          variant="primary"
          autoFocus={!isNarrowWidth}
          style={demoButtonStyle}
          data-tour="prova-demo"
          onPress={() => {
            // Ogni ingresso in demo — da qui o dalla landing con `?demo=1` —
            // apre il dialogo di orientamento "Ambiente dimostrativo".
            segnaBenvenutoDemoInAttesa();
            dispatch(createBudget({ testMode: true }));
          }}
        >
          <Trans>Try the demo</Trans>
        </Button>
        <Text
          style={{
            color: theme.pageTextLight,
            fontSize: 12.5,
            textAlign: 'center',
          }}
        >
          <Trans>
            One click and you are inside a budget already filled with accounts
            and transactions. Nothing to install, nothing to configure.
          </Trans>
        </Text>
      </View>

      <View
        className={entranceClass(450)}
        style={{
          width: '100%',
          // Wider than the demo button on purpose: the three secondary links
          // sit on one row and were being clipped at 400px.
          maxWidth: 480,
          alignItems: 'center',
          gap: isNarrowWidth ? 10 : 4,
          flexShrink: 0,
          marginTop: 18,
        }}
      >
        <Text style={{ color: theme.pageTextLight, fontSize: 13 }}>
          <Trans>Or, if you would rather start from your own data:</Trans>
        </Text>
        <View
          style={{
            flexDirection: isNarrowWidth ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: isNarrowWidth ? 8 : 4,
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Button
            variant="bare"
            style={secondaryStyle}
            onPress={() => dispatch(createBudget({}))}
          >
            <Trans>Start an empty budget</Trans>
          </Button>
          <Button
            variant="bare"
            style={secondaryStyle}
            onPress={() => dispatch(pushModal({ modal: { name: 'import' } }))}
          >
            <Trans>Import my budget</Trans>
          </Button>
          {!serverURL && (
            <Button
              variant="bare"
              style={secondaryStyle}
              onPress={() => navigate('/config-server')}
            >
              <Trans>Connect a sync server</Trans>
            </Button>
          )}
        </View>
      </View>
    </View>
  );
}
